/**
 * DOM-free ProseMirror-JSON → HTML serializer for crawler-facing SSR.
 *
 * The article editor stores a tiptap (ProseMirror) JSON document. tiptap's own
 * `generateHTML` needs a DOM (window) and cannot run in Node SSR, so the crawler
 * payload previously showed only a truncated plain-text summary (~60% of the
 * body). This walks the JSON and emits semantic HTML directly — no DOM — so the
 * SSR HTML carries the FULL article body for search/LLM crawlers.
 *
 * Server-only: the browser SPA still renders the live tiptap body. Lives in
 * `shared` next to `extractSummary` (same defensive-walk family).
 *
 * Coverage matches the editor's extension set (StarterKit h1–3 + lists + quote/
 * code + hr/hardBreak, Highlight, ImageWithCaption, Table). Anything unknown is
 * recursed into (never dropped, never thrown). Body headings are demoted +1 so
 * the rendered page keeps a single <h1> (the article title).
 */
/** Recursion-depth cap — defends the walk against pathological / DoS bodies. */
const MAX_DEPTH = 200;
/** Escape text for an HTML text node. */
function escText(value) {
    return value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
/** Escape a value for an HTML attribute (double-quoted). */
function escAttr(value) {
    return escText(value).replace(/"/g, '&quot;');
}
/** Allow only http(s)/mailto or root-relative links; else null (drops javascript: etc.). */
function sanitizeHref(href) {
    if (typeof href !== 'string')
        return null;
    const v = href.trim();
    if (v.startsWith('/'))
        return v;
    return /^(https?:|mailto:)/i.test(v) ? v : null;
}
/** Allow only http(s) or data:image sources; else null. */
function sanitizeSrc(src) {
    if (typeof src !== 'string')
        return null;
    const v = src.trim();
    return /^(https?:\/\/|data:image\/)/i.test(v) ? v : null;
}
const MARK_TAGS = {
    bold: 'strong',
    italic: 'em',
    strike: 's',
    code: 'code',
    underline: 'u',
    highlight: 'mark',
};
/** Wrap already-escaped text in its marks (link href sanitized; invalid → dropped). */
function wrapMarks(escaped, marks) {
    let open = '';
    let close = '';
    for (const mark of marks) {
        if (typeof mark !== 'object' || mark === null)
            continue;
        const m = mark;
        if (m.type === 'link') {
            const href = sanitizeHref(m.attrs?.href);
            if (!href)
                continue;
            open += `<a href="${escAttr(href)}" rel="nofollow noopener">`;
            close = `</a>${close}`;
            continue;
        }
        const tag = m.type ? MARK_TAGS[m.type] : undefined;
        if (!tag)
            continue;
        open += `<${tag}>`;
        close = `</${tag}>${close}`;
    }
    return open + escaped + close;
}
/** Render an array of child nodes. */
function renderContent(content, depth) {
    if (!Array.isArray(content))
        return '';
    let out = '';
    for (const child of content)
        out += renderNode(child, depth + 1);
    return out;
}
function renderNode(node, depth) {
    if (depth > MAX_DEPTH)
        return '';
    if (typeof node !== 'object' || node === null)
        return '';
    const n = node;
    // Text node — escape + wrap in marks.
    if (typeof n.text === 'string') {
        const escaped = escText(n.text);
        return Array.isArray(n.marks) && n.marks.length ? wrapMarks(escaped, n.marks) : escaped;
    }
    const inner = () => renderContent(n.content, depth);
    switch (n.type) {
        case 'paragraph':
            return `<p>${inner()}</p>`;
        case 'heading': {
            // Demote +1 (editor h1→h2 … h3→h4) so the page keeps a single <h1>.
            const level = Math.min(4, Math.max(2, Number(n.attrs?.level ?? 1) + 1));
            return `<h${level}>${inner()}</h${level}>`;
        }
        case 'bulletList':
            return `<ul>${inner()}</ul>`;
        case 'orderedList':
            return `<ol>${inner()}</ol>`;
        case 'listItem':
            return `<li>${inner()}</li>`;
        case 'blockquote':
            return `<blockquote>${inner()}</blockquote>`;
        case 'codeBlock':
            return `<pre><code>${inner()}</code></pre>`;
        case 'horizontalRule':
            return '<hr>';
        case 'hardBreak':
            return '<br>';
        case 'image': {
            const src = sanitizeSrc(n.attrs?.src);
            if (!src)
                return '';
            const alt = escAttr(String(n.attrs?.alt ?? ''));
            const caption = String(n.attrs?.caption ?? '').trim();
            const img = `<img src="${escAttr(src)}" alt="${alt}">`;
            return caption
                ? `<figure>${img}<figcaption>${escText(caption)}</figcaption></figure>`
                : `<figure>${img}</figure>`;
        }
        case 'table':
            return `<table><tbody>${inner()}</tbody></table>`;
        case 'tableRow':
            return `<tr>${inner()}</tr>`;
        case 'tableHeader':
            return `<th>${inner()}</th>`;
        case 'tableCell':
            return `<td>${inner()}</td>`;
        default:
            // Unknown wrapper (incl. 'doc') — recurse into children.
            return inner();
    }
}
/**
 * Serialize a tiptap document to crawler-facing HTML.
 *
 * @param body  tiptap JSON doc (opaque). Non-`doc` input yields "".
 */
export function serializeProseMirror(body) {
    if (typeof body !== 'object' || body === null)
        return '';
    const doc = body;
    if (doc.type !== 'doc')
        return '';
    return renderContent(doc.content, 0);
}
/**
 * 본문 맨 앞의 h1 을 뗀다 — **제목 바로 아래의 제목은 제목이 둘로 보인다.**
 *
 * 아티클 상세는 페이지 제목(h1, 24px)을 이미 그리고, 본문 h1 도 같은 24px 다.
 * 글쓴이가 본문 첫 줄에 큰 제목을 또 두면 태그 알약 아래에 제목이 한 번 더
 * 나온 것처럼 읽힌다(실측 「내가 본 영상과 느낀 점」). 저장된 본문은 그대로 두고
 * 그릴 때만 뗀다. 첫 블록이 h1 일 때만이고, 그 뒤의 h1 은 절 제목이라 둔다.
 *
 * SSR(public-views)과 SPA(article-detail)가 같은 함수를 쓴다.
 */
export function dropLeadingHeading(body) {
    if (typeof body !== 'object' || body === null)
        return body;
    const doc = body;
    if (doc.type !== 'doc' || !Array.isArray(doc.content) || doc.content.length === 0)
        return body;
    const first = doc.content[0];
    if (first?.type !== 'heading' || first.attrs?.level !== 1)
        return body;
    return { ...doc, content: doc.content.slice(1) };
}
//# sourceMappingURL=prosemirror-html.js.map